// Define autoscroll plugin
const EmblaCarouselAutoscroll = (options = {}) => {
  const {
    playOnInit = true,
    delay = 3000,
    stopOnInteraction = false,
    stopOnMouseEnter = false,
    stopOnFocusIn = false,
    rootNode = (emblaRoot) => emblaRoot.parentElement,
  } = options

  let interval = 0
  let emblaApi
  let root
  let playing = false

  const play = () => {
    stop()
    playing = true
    interval = window.setInterval(() => {
      if (emblaApi.canScrollNext()) {
        emblaApi.scrollNext()
      } else {
        emblaApi.scrollTo(0)
      }
    }, delay)
  }

  const stop = () => {
    playing = false
    window.clearInterval(interval)
  }

  const onVisibilityChange = () => {
    if (document.hidden) stop()
    else if (playing) play()
  }

  const onMouseEnter = () => {
    if (stopOnMouseEnter) stop()
  }

  const onFocusIn = () => {
    if (stopOnFocusIn) stop()
  }

  const onInteraction = () => {
    if (stopOnInteraction) stop()
  }

  return {
    name: 'autoscroll',
    options,
    init(embla) {
      emblaApi = embla
      root = rootNode(embla.rootNode())
      root.addEventListener('mouseenter', onMouseEnter)
      root.addEventListener('focusin', onFocusIn)
      embla.on('pointerDown', onInteraction)
      embla.on('select', onInteraction)
      document.addEventListener('visibilitychange', onVisibilityChange)
      if (playOnInit) play()
    },
    destroy() {
      stop()
      root.removeEventListener('mouseenter', onMouseEnter)
      root.removeEventListener('focusin', onFocusIn)
      emblaApi.off('pointerDown', onInteraction)
      emblaApi.off('select', onInteraction)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    },
    play,
    stop,
    isPlaying: () => playing
  }
}

const addPlayBtnListeners = (emblaApi, playBtn) => {
  const togglePlayBtnState = (emblaApi) => {
    const autoScroll = emblaApi?.plugins()?.autoScroll
    if (!autoScroll) return

    const buttonText = autoScroll.isPlaying() ? 'Start' : 'Stop'
    playBtn.innerHTML = buttonText
  }

  const onPlayBtnClick = () => {
    const autoScroll = emblaApi?.plugins()?.autoScroll
    if (!autoScroll) return

    const playOrStop = autoScroll.isPlaying()
      ? autoScroll.stop
      : autoScroll.play
    playOrStop()
  }

  playBtn.addEventListener('click', onPlayBtnClick)
  emblaApi
    .on('autoScroll:play', togglePlayBtnState)
    .on('autoScroll:stop', togglePlayBtnState)
    .on('reInit', togglePlayBtnState)

  return () => {
    playBtn.removeEventListener('click', onPlayBtnClick)
    emblaApi
      .off('autoScroll:play', togglePlayBtnState)
      .off('autoScroll:stop', togglePlayBtnState)
      .off('reInit', togglePlayBtnState)
  }
}

export const addNavBtnListeners = (emblaApi, ...navButtons) => {
  const onNavClick = () => {
    const autoScroll = emblaApi?.plugins()?.autoScroll
    if (!autoScroll) return

    const resetOrStop =
      autoScroll.options.stopOnInteraction === false
        ? autoScroll.reset
        : autoScroll.stop

    resetOrStop()
  }

  navButtons.forEach((navButton) =>
    navButton.addEventListener('click', onNavClick, true)
  )

  return () => {
    navButtons.forEach((navButton) =>
      navButton.removeEventListener('click', onNavClick, true)
    )
  }
}


// Define Preview and Next Button for embla slider
const addTogglePrevNextBtnsActive = (emblaApi, prevBtn, nextBtn) => {
  const togglePrevNextBtnsState = () => {
    // Enable or disable the previous button
    if (emblaApi.canScrollPrev()) {
      prevBtn.removeAttribute('disabled');
    } else {
      prevBtn.setAttribute('disabled', 'disabled');
    }

    // Enable or disable the next button
    if (emblaApi.canScrollNext()) {
      nextBtn.removeAttribute('disabled');
    } else {
      nextBtn.setAttribute('disabled', 'disabled');
    }
  };

  // Attach event listeners for state changes
  emblaApi
    .on('select', togglePrevNextBtnsState)
    .on('init', togglePrevNextBtnsState)
    .on('reInit', togglePrevNextBtnsState);

  return () => {
    // Reset button states
    prevBtn.removeAttribute('disabled');
    nextBtn.removeAttribute('disabled');
  };
};
// Define click handlers for embla slider
const addPrevNextBtnsClickHandlers = (emblaApi, prevBtn, nextBtn) => {
  const scrollPrev = () => {
    emblaApi.scrollPrev();
  };

  const scrollNext = () => {
    emblaApi.scrollNext();
  };

  // Add click event listeners
  prevBtn.addEventListener('click', scrollPrev, false);
  nextBtn.addEventListener('click', scrollNext, false);

  // Initialize toggle button state management
  const removeTogglePrevNextBtnsActive = addTogglePrevNextBtnsActive(
    emblaApi,
    prevBtn,
    nextBtn
  );

  return () => {
    // Clean up event listeners and states
    removeTogglePrevNextBtnsActive();
    prevBtn.removeEventListener('click', scrollPrev, false);
    nextBtn.removeEventListener('click', scrollNext, false);
  };
};
// Helper function to create dots for embler Slider
const createDots = (dots, slider) => {
  const dotsArray = [...dots.children];
  
  // Add accessibility attributes to each dot
  dotsArray.forEach((dot, index) => {
    dot.setAttribute('role', 'button');        
    dot.setAttribute('aria-label', `Showing Slide ${index + 1} of ${dotsArray.length}`);
  });

  const updateDots = () => {
    const selectedIndex = slider.selectedScrollSnap();
    dotsArray.forEach((dot, index) => {
      dot.classList.toggle('is-selected', index === selectedIndex);      
      dot.setAttribute('aria-label', `Showing Slide ${index + 1} of ${dotsArray.length}`);
    });
  };
  
  slider
    .on('select', updateDots)
    .on('init', updateDots)
    .on('reInit', updateDots);
};
// Handle Embla Slider
class CarouselInitializer {
  static initCarousels() {
    // Marketing Bar Carousel
    const mbNode = document.querySelector('.promotional-bar');
    if (mbNode) {
      const mbViewportNode = mbNode.querySelector('.embla__viewport');
      const mbOptions = {
        active: false,
        loop: true,
        breakpoints: {
          '(max-width: 768px)': { active: true, loop: true },
        }
      };
      const marketBar = EmblaCarousel(mbViewportNode, mbOptions, [EmblaCarouselAutoplay()]);
    }

    // Main Image Sliders
    const carousels = document.querySelectorAll('.embla');
    carousels.forEach(carousel => {
      const viewportNode = carousel.querySelector('.embla__viewport');
      const prevButton = carousel.querySelector('.embla__button--prev');
      const nextButton = carousel.querySelector('.embla__button--next');
      const dots = carousel.querySelector('.embla__dots');

      // Get options from HTML attributes
      const loop = carousel.getAttribute('data-loop') === 'true';
      const autoplayDelay = parseInt(carousel.getAttribute('data-autoplay-delay'), 10) || 3000; // Default to 3000ms
      const showDots = carousel.getAttribute('data-show-dots') === 'true';
      const showArrows = carousel.getAttribute('data-show-arrows') === 'true';
      const autoplayEnabled = carousel.getAttribute('data-autoplay') === 'true'; // Check if autoplay is enabled
      const fadeEnabled = carousel.getAttribute('data-fade') === 'true'; // Check if fade transition is enabled

      const options = {
        active: true,
        loop: loop,
        slidesToScroll: 1,
      };

      // Prepare plugins array
      const plugins = [];
      
      // Add autoplay plugin if enabled
      if (autoplayEnabled) {
        const autoplayOptions = {
        delay: autoplayDelay, // Set delay time in milliseconds
        };
        plugins.push(EmblaCarouselAutoplay(autoplayOptions));
      }
      
      // Add fade plugin if enabled
      if (fadeEnabled && typeof EmblaCarouselFade === 'function') {
        plugins.push(EmblaCarouselFade());
        carousel.classList.add('embla--fade'); // Add fade class for CSS styling
      }
      
      const slider = EmblaCarousel(viewportNode, options, plugins);

      // Manual fade effect no longer needed as we're using the official plugin
      // This block can be removed when official plugin is working correctly
      if (fadeEnabled && slider && typeof EmblaCarouselFade !== 'function') {
        const slides = viewportNode.querySelectorAll('.embla__slide');
        
        // Initialize all slides
        slides.forEach((slide, index) => {
          slide.style.opacity = index === 0 ? '1' : '0';
          slide.style.transition = 'opacity 0.5s ease-in-out';
          slide.style.position = 'absolute';
          slide.style.left = '0';
          slide.style.right = '0';
        });

        // Update slide visibility on slide change
        slider.on('select', () => {
          const selectedIndex = slider.selectedScrollSnap();
          slides.forEach((slide, index) => {
            slide.style.opacity = index === selectedIndex ? '1' : '0';
            slide.style.zIndex = index === selectedIndex ? '1' : '0';
          });
        });
      }

      if (showArrows && prevButton && nextButton) {
        const removePrevNextHandlers = addPrevNextBtnsClickHandlers(
          slider,
          prevButton,
          nextButton
        );
        slider.on('destroy', removePrevNextHandlers);
      }

      if (showDots && dots) {
        // Create dot buttons
        const slides = viewportNode.querySelectorAll('.embla__slide');
        slides.forEach(() => {
          const dot = document.createElement('button');
          dot.classList.add('embla__dot');
          dots.appendChild(dot);
        });

        // Add click handlers to dots
        const dotsArray = [...dots.children];
        dotsArray.forEach((dot, index) => {
          dot.addEventListener('click', () => slider.scrollTo(index));
        });

        createDots(dots, slider);
      }
    });

    // Sub Collection Navigation Carousel
    const subCollectionCarousels = document.querySelectorAll('.sub-collection-wrapper');
    subCollectionCarousels.forEach(carousel => {
      const viewportNode = carousel.querySelector('.sub-collection-wrapper__inner');
      const prevButton = carousel.querySelector('.embla__button--prev');
      const nextButton = carousel.querySelector('.embla__button--next');
      if (!viewportNode) return;

      const options = {
        align: 'start',
        containScroll: 'trimSnaps',
        dragFree: true, // Set to false for snapped sliding
        draggable: true,
        loop: false,
        breakpoints: {
          '(max-width: 749px)': { slidesToShow: 2 },
          '(min-width: 750px)': { slidesToShow: 'auto' }
        }
      };
      
      const slider = EmblaCarousel(viewportNode, options);

      // Add prev/next button handlers
      if (prevButton && nextButton) {
        const removePrevNextHandlers = addPrevNextBtnsClickHandlers(
          slider,
          prevButton,
          nextButton
        );
        slider.on('destroy', removePrevNextHandlers);
      }

      // Scroll to active item if exists
      const scrollToActive = () => {
        const slides = viewportNode.querySelectorAll('li');
        slides.forEach((slide, index) => {
          if (slide.querySelector('.active')) {
            slider.scrollTo(index);
          }
        });
      };

      // Initialize scroll to active
      slider.on('init', scrollToActive);
      slider.on('reInit', scrollToActive);
    });
  
    // Initialize Promotional Grid Sliders
    this.initPromoSliders();
  }

  static initPromoSliders() {
    const promoSliders = document.querySelectorAll('.embla_promo_slider');
    
    promoSliders.forEach(slider => {
      // Get slider elements
      const viewportNode = slider.querySelector('.embla_promo_slider__viewport');
      if (!viewportNode) return; // Skip if viewport not found
      
      const prevButton = slider.querySelector('.embla_promo_slider__button--prev');
      const nextButton = slider.querySelector('.embla_promo_slider__button--next');
      const dots = slider.querySelector('.embla_promo_slider__dots');
      
      // Get options from HTML data attributes
      const itemInRow = slider.getAttribute('data-item-row') || 'two'; // Default to four
      const loop = slider.getAttribute('data-loop') === 'true';
      const autoplay = slider.getAttribute('data-autoplay') === 'true';
      // const autoplayDelay = parseInt(slider.getAttribute('data-autoplay-delay'), 10) || 3000;
      const showDots = slider.getAttribute('data-show-dots') === 'true';
      const showArrows = slider.getAttribute('data-show-arrows') === 'true';
      const fade = slider.getAttribute('data-fade') === 'true';
      const speed = parseInt(slider.getAttribute('data-speed'), 10) || 300; // Default speed of 300ms
      
      // Determine slides per view based on item_in_row
      let slidesPerView = 2; // Default for 'four'
      switch(itemInRow) {
        case 'one':
          slidesPerView = 1;
          break;
        case 'two':
          slidesPerView = 2;
          break;
        case 'three':
          slidesPerView = 3;
          break;
        case 'four':
          slidesPerView = 4;
          break;
      }
      
      // Create options object
      const options = {
        loop: loop,
        align: 'start',
        skipSnaps: false,
        slidesToScroll: 2,
        speed: speed, // Add speed option
        breakpoints: {
          '(max-width: 749px)': { slidesToScroll: 1, containScroll: 'trimSnaps' },
          '(min-width: 750px) and (max-width: 989px)': { slidesToScroll: 1, slidesPerView: Math.min(slidesPerView, 2) },
          '(min-width: 990px)': { slidesToScroll: 1, slidesPerView: 1 }
        }
      };
      
      // Set up plugins
      const plugins = [];
      
      // Add autoplay plugin if enabled
      if (autoplay && typeof EmblaCarouselAutoplay === 'function') {
        plugins.push(EmblaCarouselAutoplay({ stopOnInteraction: true }));
      }
      
      // Add fade plugin if enabled
      if (fade && typeof EmblaCarouselFade === 'function') {
        plugins.push(EmblaCarouselFade());
        slider.classList.add('embla--fade');
      }
      
      // Initialize Embla
      const emblaApi = EmblaCarousel(viewportNode, options, plugins);
      
      // Add previous/next button handlers if requested
      if (showArrows && prevButton && nextButton) {
        const removePrevNextHandlers = addPrevNextBtnsClickHandlers(
          emblaApi,
          prevButton,
          nextButton
        );
        emblaApi.on('destroy', removePrevNextHandlers);
      }
      
      // Add dots if requested
      if (showDots && dots) {
        const addDots = (emblaApi, dotsNode) => {
          const slides = emblaApi.slideNodes();
          dotsNode.innerHTML = '';
          
          slides.forEach((_, index) => {
            const dot = document.createElement('button');
            dot.classList.add('embla_promo_slider__dot');
            dot.setAttribute('type', 'button');
            dotsNode.appendChild(dot);
            
            dot.addEventListener('click', () => emblaApi.scrollTo(index), false);
          });
          
          const dotsArray = Array.from(dotsNode.querySelectorAll('.embla_promo_slider__dot'));
          
          const updateDots = () => {
            const selectedIndex = emblaApi.selectedScrollSnap();
            dotsArray.forEach((dot, i) => {
              dot.classList.toggle('is-selected', i === selectedIndex);
            });
          };
          
          emblaApi
            .on('select', updateDots)
            .on('init', updateDots)
            .on('reInit', updateDots);
            
          return () => {
            dotsArray.forEach((dot, index) => {
              dot.removeEventListener('click', () => emblaApi.scrollTo(index), false);
            });
          };
        };
        
        const removeDots = addDots(emblaApi, dots);
        emblaApi.on('destroy', removeDots);
      }
    });
  }

  static initProductSliders() {
    const productSliders = document.querySelectorAll('.embla_product_slider');
    
    productSliders.forEach(slider => {
      // Get slider elements
      const viewportNode = slider.querySelector('.embla__viewport');
      if (!viewportNode) return; // Skip if viewport not found
      
      const prevButton = slider.querySelector('.embla_promo_slider__button--prev');
      const nextButton = slider.querySelector('.embla_promo_slider__button--next');
      const dots = slider.querySelector('.embla_promo_slider__dots');
      const playButton = slider.querySelector('.embla__autoplay');
      
      // Get options from HTML data attributes
      const itemInRow = parseInt(slider.getAttribute('data-item-row')) || 4; // Default to 4
      const loop = slider.getAttribute('data-loop') === 'true';
      const autoplay = slider.getAttribute('data-autoplay') !== 'false';
      const showDots = slider.getAttribute('data-show-dots') === 'true';
      const showArrows = slider.getAttribute('data-show-arrows') === 'true';
      const speed = parseInt(slider.getAttribute('data-speed'), 10) || 300;
      
      // Create options object
      const options = {
        loop: loop,
        align: 'start',
        skipSnaps: false,
        speed: speed,
        breakpoints: {
          '(max-width: 749px)': { slidesToScroll: 1, slidesPerView: 2 },
          '(min-width: 750px) and (max-width: 989px)': { slidesToScroll: 1, slidesPerView: 3 },
          '(min-width: 990px)': { slidesToScroll: 1, slidesPerView: itemInRow }
        }
      };
      
      // Set up plugins
      const plugins = [];
      
      // Add autoscroll plugin if enabled
      if (autoplay) {
        plugins.push(EmblaCarouselAutoscroll({
          playOnInit: true,
          stopOnInteraction: false,
          stopOnMouseEnter: false,
          stopOnFocusIn: false
        }));
      }
      
      // Initialize Embla
      const emblaApi = EmblaCarousel(viewportNode, options, plugins);
      
      // Add play button if it exists
      if (playButton) {
        const removePlayBtnListeners = addPlayBtnListeners(emblaApi, playButton);
        emblaApi.on('destroy', removePlayBtnListeners);
      }
      
      // Add previous/next button handlers if requested
      if (showArrows && prevButton && nextButton) {
        const removePrevNextHandlers = addPrevNextBtnsClickHandlers(
          emblaApi,
          prevButton,
          nextButton
        );
        emblaApi.on('destroy', removePrevNextHandlers);
      }
      
      // Add dots if requested
      if (showDots && dots) {
        const addDots = (emblaApi, dotsNode) => {
          const slides = emblaApi.slideNodes();
          dotsNode.innerHTML = '';
          
          slides.forEach((_, index) => {
            const dot = document.createElement('button');
            dot.classList.add('embla_product_slider__dot');
            dot.setAttribute('type', 'button');
            dotsNode.appendChild(dot);
            
            dot.addEventListener('click', () => emblaApi.scrollTo(index), false);
          });
          
          const dotsArray = Array.from(dotsNode.querySelectorAll('.embla_product_slider__dot'));
          
          const updateDots = () => {
            const selectedIndex = emblaApi.selectedScrollSnap();
            dotsArray.forEach((dot, i) => {
              dot.classList.toggle('is-selected', i === selectedIndex);
            });
          };
          
          emblaApi
            .on('select', updateDots)
            .on('init', updateDots)
            .on('reInit', updateDots);
            
          return () => {
            dotsArray.forEach((dot, index) => {
              dot.removeEventListener('click', () => emblaApi.scrollTo(index), false);
            });
          };
        };
        
        const removeDots = addDots(emblaApi, dots);
        emblaApi.on('destroy', removeDots);
      }
    });
  }
}
class FooterAccordion {
  constructor() {    
    document.addEventListener('DOMContentLoaded', () => {      
      this.accordions = document.querySelectorAll('.footer-accordion_item');
      
      if (this.accordions.length) {
        this.init();
      }
    });
  }

  init() {
    this.accordions.forEach(accordion => {
      const header = accordion.querySelector('.accordion-footer_header');
      const content = accordion.querySelector('.accordion-footer_content');
      
      if (header && content) {
        header.addEventListener('click', () => {          
          if (window.innerWidth < 768) {
            header.classList.toggle('active');
            content.classList.toggle('active');
          }
        });
      }
    });
  }
}
class MegaMenuInitializer {
  static init() {
    const menuItems = document.querySelectorAll('.list-menu > li');
    
    if (!menuItems.length) return;
    
    menuItems.forEach(item => {
      const megaMenuContent = item.querySelector('.mega-menu__content');
      
      if (!megaMenuContent) return;
      
      // Add hover event listeners
      item.addEventListener('mouseenter', () => {
        megaMenuContent.classList.add('is-active');
      });
      
      item.addEventListener('mouseleave', () => {
        megaMenuContent.classList.remove('is-active');
      });
      
      // Handle focus for accessibility
      item.addEventListener('focusin', () => {
        megaMenuContent.classList.add('is-active');
      });
      
      item.addEventListener('focusout', (event) => {
        // Only hide if focus moves outside this menu item
        if (!item.contains(event.relatedTarget)) {
          megaMenuContent.classList.remove('is-active');
        }
      });
    });
  }
}
class AccordionGlobal {
  constructor(container) {
    this.container = container;
    this.controls = this.container.querySelectorAll('.accordion-control');
    this.init();
  }
  
  init() {
    this.controls.forEach(control => {
      const content = control.nextElementSibling;
      if (content && content.classList.contains('accordion-content')) {
        content.classList.add('is-hidden'); // Start with content hidden
        
        control.addEventListener('click', this.toggleAccordion.bind(this, control));
      }
    });
  }
  
  toggleAccordion(control) {
    const content = control.nextElementSibling;
    if (!content || !content.classList.contains('accordion-content')) return;
    
    const isOpen = !content.classList.contains('is-hidden');
        
    this.closeAll();

    if (!isOpen) {
      control.classList.add('active');
      content.classList.remove('is-hidden');
      content.classList.add('is-visible');
            
      const iconWrapper = control.querySelector('.icon-wrapper');
      if (iconWrapper) {
        iconWrapper.classList.add('open');
      }
    }
  }
  
  closeAll() {
    this.controls.forEach(control => {
      control.classList.remove('active');
      const content = control.nextElementSibling;
      if (content && content.classList.contains('accordion-content')) {
        content.classList.remove('is-visible');
        content.classList.add('is-hidden');
      }
      
      const iconWrapper = control.querySelector('.icon-wrapper');
      if (iconWrapper) {
        iconWrapper.classList.remove('open');
      }
    });
  }
  
  openAll() {
    this.controls.forEach(control => {
      control.classList.add('active');
      const content = control.nextElementSibling;
      if (content && content.classList.contains('accordion-content')) {
        content.classList.remove('is-hidden');
        content.classList.add('is-visible');
      }
      
      const iconWrapper = control.querySelector('.icon-wrapper');
      if (iconWrapper) {
        iconWrapper.classList.add('open');
      }
    });
  }
  
  open(index) {
    if (index >= 0 && index < this.controls.length) {
      this.toggleAccordion(this.controls[index]);
    }
  }
}

class ProductOverviewTabs {
  constructor() {
    this.tabButtons = document.querySelectorAll('.nav--link');
    this.tabPanels = document.querySelectorAll('.tab--panel');
    this.accordionControls = document.querySelectorAll('.accordion-control');
    this.accordionPanels = document.querySelectorAll('.accordion-panel');
    
    this.init();
  }

  init() {
    // Desktop tab functionality
    this.tabButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const targetTab = e.currentTarget.dataset.tab;
        
        // Remove active class from all buttons and panels
        this.tabButtons.forEach(btn => btn.classList.remove('active'));
        this.tabPanels.forEach(panel => panel.classList.remove('active'));
        
        // Add active class to clicked button and corresponding panel
        e.currentTarget.classList.add('active');
        document.getElementById(`panel-${targetTab}`).classList.add('active');
      });
    });
  }
}
class HiddenItemsHandler {
  static readMore(button) {
    const items = button.parentElement.querySelectorAll('.hidden-item');
    items.forEach(item => {
      item.style.display = 'block';
    });
    button.style.display = 'none';
  }

  static init() {
    // Find all buttons with onclick="readMore(this)"
    const buttons = document.querySelectorAll('button[onclick*="readMore"]');
    buttons.forEach(button => {
      // Remove the inline onclick
      button.removeAttribute('onclick');
      // Add event listener
      button.addEventListener('click', () => this.readMore(button));
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  MegaMenuInitializer.init();
  
  const accordionContainers = document.querySelectorAll('.section-accordion');
  
  accordionContainers.forEach(container => {
    new AccordionGlobal(container);
  });

  new ProductOverviewTabs();
  HiddenItemsHandler.init();
});

class PopoverHandler {
  constructor() {
    this.init();
  }

  init() {
    document.addEventListener('DOMContentLoaded', () => {
      const productCards = document.querySelectorAll('.popover-container');
      
      productCards.forEach(card => {
        const popover = card.querySelector('.item-popover');
        const popoverItems = card.querySelectorAll('.product-attribute-item');

        popoverItems.forEach(item => {
          item.addEventListener('mouseenter', (e) => {
            const flavour = e.target.getAttribute('data-flavour');
            const rect = e.target.getBoundingClientRect();
            const containerRect = card.getBoundingClientRect();
            
            popover.textContent = flavour;
            popover.style.display = 'block';
            
            // Calculate position relative to the container
            const popoverWidth = popover.offsetWidth;
            const itemWidth = rect.width;
            const leftOffset = (itemWidth - popoverWidth) / 2;
            
            // Position relative to the container
            const top = rect.top - containerRect.top - popover.offsetHeight - 5;
            const left = rect.left - containerRect.left + leftOffset;
            
            popover.style.top = `${top}px`;
            popover.style.left = `${left}px`;
          });

          item.addEventListener('mouseleave', () => {
            popover.style.display = 'none';
          });
        });
      });
    });
  }
}

class Themejs {
  static init() {
    CarouselInitializer.initCarousels();
    new FooterAccordion();
    CarouselInitializer.initProductSliders();
    new PopoverHandler(); 
  }
}

Themejs.init();